import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";

const AUTO_DURATION = 5000;

const ITEMS = [
  {
    labelKey: "live_interaction_1_label",
    titleKey: "live_interaction_1_title",
    bodyKey: "live_interaction_1_body",
    img: "/images/software/live/interaction-slide1.png",
  },
  {
    labelKey: "live_interaction_2_label",
    titleKey: "live_interaction_2_title",
    bodyKey: "live_interaction_2_body",
    img: "/images/software/live/interaction-slide2.jpg",
  },
  {
    labelKey: "live_interaction_3_label",
    titleKey: "live_interaction_3_title",
    bodyKey: "live_interaction_3_body",
    img: "/images/software/live/interaction-slide3.png",
  },
  {
    labelKey: "live_interaction_4_label",
    titleKey: "live_interaction_4_title",
    bodyKey: "live_interaction_4_body",
    img: "/images/software/live/interaction-slide4.png",
  },
] as const;

const CARD_W = 1196;
const GAP = 32;
const STRIDE = CARD_W + GAP;

type SliderProps = {
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  prev: () => void;
  next: () => void;
};

function Desktop({ activeIndex, setActiveIndex, prev, next }: SliderProps) {
  const { t } = useTrans(["software"]);
  return (
    <div className="hidden md:block">
      <div className="mx-auto w-full max-w-[1920px] px-4">
        <h2 className="text-center text-[32px] font-400 leading-[40px] text-coolgray-90">
          {t("live_interaction_heading")}
        </h2>
      </div>

      {/* 3-up peek carousel — 양쪽 fade */}
      <div
        className="relative mx-auto mt-8 w-full max-w-[1920px] py-[24px]"
        style={{
          overflowX: "clip",
          maskImage:
            "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
        }}
      >
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            gap: `${GAP}px`,
            paddingLeft: `calc((100% - ${CARD_W}px) / 2)`,
            paddingRight: `calc((100% - ${CARD_W}px) / 2)`,
            transform: `translateX(calc(-${activeIndex} * ${STRIDE}px))`,
          }}
        >
          {ITEMS.map((item, i) => {
            const isActive = i === activeIndex;
            return (
              <article
                key={item.labelKey}
                onClick={() => setActiveIndex(i)}
                style={{ width: `${CARD_W}px` }}
                className={cn(
                  "flex shrink-0 cursor-pointer flex-row gap-[32px] overflow-hidden border border-coolgray-20 bg-white p-[24px] shadow-[0px_8px_16px_0px_rgba(0,0,0,0.15)] transition-all duration-300",
                  isActive
                    ? "rounded-[24px] opacity-100"
                    : "rounded-[16px] opacity-40",
                )}
              >
                {/* 좌: 텍스트 */}
                <div className="flex flex-1 flex-col items-center justify-center gap-[32px] self-stretch px-[40px]">
                  <div className="flex h-[48px] w-fit items-center justify-center rounded-full bg-blue-10 px-[24px]">
                    <span className="whitespace-nowrap text-[18px] font-600 leading-[24px] tracking-[0.1px] text-blue-60">
                      {t(item.labelKey)}
                    </span>
                  </div>
                  <div className="flex w-full flex-col gap-[16px] text-center">
                    <h3 className="whitespace-pre-line text-[24px] font-400 leading-[32px] text-coolgray-90">
                      {t(item.titleKey)}
                    </h3>
                    <p className="whitespace-pre-line text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
                      {t(item.bodyKey)}
                    </p>
                  </div>
                </div>

                {/* 우: 이미지 */}
                <div className="relative h-[558px] flex-1 overflow-hidden rounded-[16px] bg-coolgray-10">
                  {item.img && (
                    <Image
                      src={item.img}
                      alt={t(item.labelKey)}
                      fill
                      sizes="(max-width: 768px) 100vw, 600px"
                      className="object-cover"
                    />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className="mx-auto mt-8 flex w-full max-w-[1920px] items-center justify-center gap-[40px] px-4">
        <div className="flex items-center gap-[12px]">
          {ITEMS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`슬라이드 ${i + 1}`}
              className={cn(
                "relative h-[8px] overflow-hidden rounded-full bg-coolgray-20 p-0 transition-all duration-300",
                i === activeIndex ? "w-[32px]" : "w-[8px]",
              )}
            >
              <div
                key={
                  i === activeIndex ? `active-${activeIndex}` : `inactive-${i}`
                }
                className={cn(
                  "absolute left-0 top-0 h-full bg-blue-60",
                  i === activeIndex
                    ? "animate-progress-linear-forwards"
                    : "hidden",
                )}
                style={
                  {
                    ["--tw-anim-dur" as string]: `${AUTO_DURATION}ms`,
                  } as React.CSSProperties
                }
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-[20px]">
          <button
            type="button"
            onClick={prev}
            className="flex size-[48px] items-center justify-center rounded-full bg-coolgray-10 transition-colors hover:bg-coolgray-20"
            aria-label="이전"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                className="text-coolgray-90"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            className="flex size-[48px] items-center justify-center rounded-full bg-coolgray-10 transition-colors hover:bg-coolgray-20"
            aria-label="다음"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                className="text-coolgray-90"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function Mobile({
  activeIndex,
  setActiveIndex,
}: Pick<SliderProps, "activeIndex" | "setActiveIndex">) {
  const { t } = useTrans(["software"]);
  return (
    <div className="md:hidden">
      <div className="px-4 text-center">
        <h2 className="whitespace-pre-line text-[24px] font-400 leading-[32px] text-coolgray-90">
          {t("live_interaction_heading")}
        </h2>
      </div>

      <div className="mt-6 overflow-x-hidden py-3">
        <div
          className="flex gap-[12px] transition-transform duration-500 ease-in-out"
          style={{
            paddingLeft: "calc((100% - 296px) / 2)",
            paddingRight: "calc((100% - 296px) / 2)",
            transform: `translateX(calc(-${activeIndex} * (296px + 12px)))`,
          }}
        >
          {ITEMS.map((item) => (
            <article
              key={item.labelKey}
              className="flex w-[296px] shrink-0 flex-col gap-[16px] overflow-hidden rounded-[16px] bg-white pb-[20px] shadow-[0px_4px_8px_0px_rgba(0,0,0,0.15)]"
            >
              <div className="relative aspect-square w-full overflow-hidden bg-coolgray-10">
                {item.img && (
                  <Image
                    src={item.img}
                    alt={t(item.labelKey)}
                    fill
                    sizes="(max-width: 768px) 296px, 600px"
                    className="object-cover"
                  />
                )}
              </div>

              <div className="flex justify-center">
                <div className="flex h-[40px] w-fit items-center justify-center rounded-full bg-blue-10 px-[16px]">
                  <span className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.1px] text-blue-60">
                    {t(item.labelKey)}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-[16px] px-[24px] text-center">
                <h3 className="text-[16px] font-500 leading-[22px] tracking-[0.15px] text-coolgray-90">
                  {t(item.titleKey)}
                </h3>
                <p className="text-[12px] font-400 leading-[16px] tracking-[0.4px] text-coolgray-70">
                  {t(item.bodyKey)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-[12px]">
        {ITEMS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActiveIndex(i)}
            aria-label={`슬라이드 ${i + 1}`}
            className={cn(
              "relative h-[8px] overflow-hidden rounded-full bg-coolgray-20 transition-all duration-300",
              i === activeIndex ? "w-[32px]" : "w-[8px]",
            )}
          >
            <div
              key={
                i === activeIndex ? `active-${activeIndex}` : `inactive-${i}`
              }
              className={cn(
                "absolute left-0 top-0 h-full bg-blue-60",
                i === activeIndex
                  ? "animate-progress-linear-forwards"
                  : "hidden",
              )}
              style={
                {
                  ["--tw-anim-dur" as string]: `${AUTO_DURATION}ms`,
                } as React.CSSProperties
              }
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LiveInteractionSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const total = ITEMS.length;
  const prev = () => setActiveIndex((p) => (p === 0 ? total - 1 : p - 1));
  const next = () => setActiveIndex((p) => (p + 1) % total);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => {
      setActiveIndex((p) => (p + 1) % total);
    }, AUTO_DURATION);
    return () => clearTimeout(timer);
  }, [activeIndex, isVisible, total]);

  return (
    <ComponentErrorBoundary>
      <section ref={sectionRef} className="w-full bg-white">
        <Desktop
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          prev={prev}
          next={next}
        />
        <Mobile activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
      </section>
    </ComponentErrorBoundary>
  );
}
